import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, money } from "@/lib/api";
import { DEFAULT_HOTEL_IMAGE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import {
  Star, MapPin, CheckCircle, ArrowLeft, FileText, Bed, CalendarBlank,
} from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

function nights(a, b) {
  try { return Math.max(Math.round((new Date(b) - new Date(a)) / 86400000), 1); } catch { return 1; }
}

function RfpDialog({ hotel }) {
  const { user } = useAuth();
  const role = user?.role;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    check_in: "", check_out: "", num_rooms: 5, nightly_budget: hotel.base_rate, notes: "",
  });

  const mut = useMutation({
    mutationFn: (body) => api.createRfp(body),
    onSuccess: (rfp) => {
      qc.invalidateQueries({ queryKey: ["rfps"] });
      toast.success("RFP submitted", { description: `Sent to ${hotel.name}. Start negotiating now.` });
      setOpen(false);
      navigate(`/negotiation/${rfp.id}`);
    },
    onError: () => toast.error("Could not submit RFP"),
  });

  const submit = () => {
    if (!form.check_in || !form.check_out) return toast.error("Pick check-in and check-out dates");
    const rn = nights(form.check_in, form.check_out) * Number(form.num_rooms);
    mut.mutate({
      hotel_id: hotel.id,
      check_in: form.check_in,
      check_out: form.check_out,
      num_rooms: Number(form.num_rooms),
      nightly_budget: Number(form.nightly_budget),
      room_nights: rn,
      notes: form.notes,
    });
  };

  const F = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const rn = form.check_in && form.check_out ? nights(form.check_in, form.check_out) * Number(form.num_rooms) : 0;

  if (role !== "company") {
    return (
      <div className="bg-[#F3F4F6] border border-[#E5E7EB] p-4 text-sm text-[#4B5563]">
        Switch to <b>Company</b> view to submit an RFP for this hotel.
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid="open-rfp-dialog-btn"
          className="w-full bg-[#002FA7] text-white py-3.5 font-semibold hover:bg-[#00227A] transition-colors active:scale-[0.98] flex items-center justify-center gap-2"
        >
          <FileText size={18} weight="bold" /> Submit RFP
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-[#111827] max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Request for Proposal</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#4B5563] -mt-2">To {hotel.name} · {hotel.city}, {hotel.state}</p>
        <div className="grid grid-cols-2 gap-4 mt-2">
          <label className="text-sm">
            <span className="overline text-[#4B5563] block mb-1">Check-in</span>
            <input data-testid="rfp-checkin" type="date" value={form.check_in} onChange={F("check_in")}
              className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
          </label>
          <label className="text-sm">
            <span className="overline text-[#4B5563] block mb-1">Check-out</span>
            <input data-testid="rfp-checkout" type="date" value={form.check_out} onChange={F("check_out")}
              className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
          </label>
          <label className="text-sm">
            <span className="overline text-[#4B5563] block mb-1">Rooms</span>
            <input data-testid="rfp-rooms" type="number" min="1" value={form.num_rooms} onChange={F("num_rooms")}
              className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
          </label>
          <label className="text-sm">
            <span className="overline text-[#4B5563] block mb-1">Target rate / night</span>
            <input data-testid="rfp-budget" type="number" min="0" value={form.nightly_budget} onChange={F("nightly_budget")}
              className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
          </label>
        </div>
        <label className="text-sm">
          <span className="overline text-[#4B5563] block mb-1">Notes</span>
          <textarea data-testid="rfp-notes" rows={3} value={form.notes} onChange={F("notes")}
            placeholder="Crew size, parking, breakfast, flexible dates…"
            className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
        </label>
        {rn > 0 && (
          <div className="bg-[#E0E7FF] text-[#002FA7] px-4 py-2.5 text-sm font-semibold flex justify-between">
            <span>{rn} total room-nights</span>
            <span>≈ {money(rn * Number(form.nightly_budget))} at target</span>
          </div>
        )}
        <DialogFooter>
          <button
            data-testid="submit-rfp-btn"
            onClick={submit}
            disabled={mut.isPending}
            className="bg-[#002FA7] text-white px-6 py-3 font-semibold hover:bg-[#00227A] transition-colors active:scale-[0.98] disabled:opacity-60"
          >
            {mut.isPending ? "Submitting…" : "Send RFP & Negotiate"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function HotelDetail() {
  const { id } = useParams();
  const { data: hotel, isLoading } = useQuery({ queryKey: ["hotel", id], queryFn: () => api.hotel(id) });

  if (isLoading) return <div className="max-w-7xl mx-auto px-8 py-20 text-[#4B5563]">Loading hotel…</div>;
  if (!hotel) return <div className="max-w-7xl mx-auto px-8 py-20">Hotel not found.</div>;

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-8">
      <Link to="/marketplace" data-testid="back-to-market" className="inline-flex items-center gap-2 text-sm text-[#4B5563] hover:text-[#002FA7] transition-colors">
        <ArrowLeft size={16} weight="bold" /> Back to marketplace
      </Link>

      <div className="grid lg:grid-cols-12 gap-8 mt-6">
        <div className="lg:col-span-8">
          <div className="border border-[#E5E7EB]">
            <img src={hotel.images?.[0] || DEFAULT_HOTEL_IMAGE} alt={hotel.name} className="w-full h-[380px] object-cover" />
          </div>
          <div className="flex items-center gap-3 mt-6">
            <span className="overline text-[#4B5563]">{hotel.brand}</span>
            <span className="flex items-center gap-1 text-sm font-semibold"><Star size={15} weight="fill" color="#D97706" /> {hotel.star_rating}</span>
          </div>
          <h1 className="font-display font-black text-4xl text-[#111827] mt-2">{hotel.name}</h1>
          <div className="flex items-center gap-1 text-[#4B5563] mt-2"><MapPin size={16} weight="fill" /> {hotel.address}</div>
          <p className="text-[#4B5563] leading-relaxed mt-5 max-w-2xl">{hotel.description}</p>

          <h2 className="font-display font-bold text-xl text-[#111827] mt-8 mb-3">Amenities</h2>
          <div className="grid sm:grid-cols-2 gap-2">
            {hotel.amenities?.map((a) => (
              <div key={a} className="flex items-center gap-2 text-sm text-[#111827]">
                <CheckCircle size={16} weight="fill" color="#002FA7" /> {a}
              </div>
            ))}
          </div>

          <h2 className="font-display font-bold text-xl text-[#111827] mt-8 mb-3">Room types</h2>
          <div className="border border-[#E5E7EB]">
            {hotel.room_types?.map((r, i) => (
              <div key={r.name} className={`flex items-center justify-between p-4 ${i > 0 ? "border-t border-[#E5E7EB]" : ""}`}>
                <div className="flex items-center gap-3">
                  <Bed size={20} weight="duotone" color="#002FA7" />
                  <div>
                    <div className="font-semibold text-[#111827]">{r.name}</div>
                    <div className="text-xs text-[#4B5563]">{r.beds}</div>
                  </div>
                </div>
                <div className="font-mono font-semibold text-[#111827]">{money(r.base_rate)}<span className="text-xs text-[#4B5563]">/nt</span></div>
              </div>
            ))}
          </div>
        </div>

        {/* sticky RFP panel */}
        <div className="lg:col-span-4">
          <div className="border border-[#111827] p-6 lg:sticky lg:top-24">
            <span className="overline text-[#4B5563]">Published rate from</span>
            <div className="font-display font-black text-4xl text-[#111827] mt-1">
              {money(hotel.base_rate)}<span className="text-base font-mono text-[#4B5563]">/night</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-[#047857] font-semibold mt-2">
              <CalendarBlank size={16} weight="fill" /> Negotiable for room blocks
            </div>
            <p className="text-xs text-[#4B5563] mt-3 mb-5">
              Managed by <b className="text-[#111827]">{hotel.owner_name}</b>. Submit an RFP to open a direct negotiation — no OTA fees.
            </p>
            <RfpDialog hotel={hotel} />
          </div>
        </div>
      </div>
    </div>
  );
}
